import React from 'react';
import styled, { css } from 'styled-components';
import { Variant } from '../../common/variant';
import { useTestId } from '../../hooks/useTestId';
import { breakpoint } from '../../Themes/screensize';
import { NotificationIcon } from '../Notification/NotificationIcon';
import { IThemePalette } from '../../Themes/interfaces';
import { transparentize } from 'polished';
import { baseFontCss } from '../../common/mixins';

export const navigationColours = (palette: IThemePalette) => ({
  navigationBar: {
    text: palette.primary.text[20],
    background: palette.neutral.white,
    selected: {
      text: palette.primary.text[100],
      border: palette.primary.first[100]
    },
    boxShadow: transparentize(0.9, palette.primary.text[75])
  }
});

interface IBaseNavigationBarProps {
  containsLogo?: boolean;
}

const BaseNavigationBar = styled.div<IBaseNavigationBarProps>`
  background-color: ${({ theme }) => theme.colours.navigationBar.background};
  width: 100%;
  height: 5rem;
  box-shadow: 0 0.25rem 0.5rem ${({ theme }) => theme.colours.navigationBar.boxShadow};
  position: sticky;
  top: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: nowrap;

  ${({ containsLogo }) =>
    containsLogo &&
    css`
      justify-content: space-between;
      & > *:first-child {
        flex-grow: 1;
        flex-basis: 0;
      }
      & > *:last-child {
        flex-grow: 1;
        flex-basis: 0;
      }
    `};

  @media (max-width: ${breakpoint.mobile}) {
    display: block;
    height: 3.5rem;
    margin-bottom: 3.5rem;
  }
`;

const FullLabelSpan = styled.span``;
const ShortLabelSpan = styled.span``;

const NavigationLinksContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  max-width: 40rem;

  @media (max-width: ${breakpoint.mobile}) {
    position: absolute;
    max-width: none;
  }
`;

interface IBaseNavigationLinkProps {
  selected?: boolean;
  visited?: boolean;
}

const BaseNavigationLink = styled.a<IBaseNavigationLinkProps>`
  ${baseFontCss};
  color: ${({ theme }) => theme.colours.navigationBar.text};
  height: 100%;
  flex: 1;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-weight: 500;
  border-bottom: 0.25rem solid;
  ${({ selected, visited, theme }) =>
    (selected || visited) &&
    css`
      border-bottom-color: ${theme.colours.navigationBar.selected.border};
      color: ${theme.colours.navigationBar.selected.text};
    `}
  cursor: pointer;
  text-decoration: none;

  ${ShortLabelSpan} {
    display: none;
  }

  @media (max-width: ${breakpoint.mobile}) {
    ${FullLabelSpan} {
      display: none;
    }

    ${ShortLabelSpan} {
      display: ${({ visited }) => (visited ? 'none' : 'initial')};
    }

    border-bottom: 0;
    border-top: 0.25rem solid;
    ${({ selected, visited, theme }) =>
      (selected || visited) &&
      css`
        border-top-color: ${theme.colours.navigationBar.selected.border};
      `}
  }
`;

export interface INavigationLinkProps extends IBaseNavigationLinkProps, React.AnchorHTMLAttributes<HTMLAnchorElement> {
  label?: string;
  shortLabel?: string;
}

export const NavigationLink: React.FC<INavigationLinkProps> = ({ label, shortLabel = label, children, visited, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'NavigationLink');
  return (
    <BaseNavigationLink visited={visited} {...rest} {...generateTestId()}>
      {visited && <NotificationIcon variant={Variant.Success} {...generateTestId('Icon')} />}
      <FullLabelSpan {...generateTestId('Full')}>{label}</FullLabelSpan>
      <ShortLabelSpan {...generateTestId('Short')}>{shortLabel}</ShortLabelSpan>
      {children}
    </BaseNavigationLink>
  );
};

export interface INavigationBarProps {
  renderLogo?: () => JSX.Element;
}

const NavigationSpacer = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
`;

// In order to center the navigation links, it is required to add an extra "div" at the end of the bar only if there is a Logo to be rendered
export const NavigationBar: React.FC<INavigationBarProps> = ({ renderLogo, children, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'NavigationBar');
  return (
    <BaseNavigationBar containsLogo={!!renderLogo} {...generateTestId()}>
      {renderLogo && <NavigationSpacer {...generateTestId('Spacer')}>{renderLogo()}</NavigationSpacer>}
      <NavigationLinksContainer {...generateTestId('Links')}>{children}</NavigationLinksContainer>
      {renderLogo && <NavigationSpacer {...generateTestId('Spacer')} />}
    </BaseNavigationBar>
  );
};
